import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useEffect, useRef, useState } from "react";

//Components
import { Modal, ModalBody, ModalFooter, Button, Form, Input, Select } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
//Helpers
import { getMessageTemplateCodes, languageOptions, getTemplatesFields } from 'Core/data/Helpers';

import SunEditor from 'suneditor-react';
import 'suneditor/src/assets/css/suneditor.css'

//Actions
import {
    clearRedux,
    messageTemplateCreateRequest,
    messageTemplateGetRequest,
    messageTemplateEditRequest,
} from 'Redux/actions';
import { get, set } from "lodash";

const CreateEditMessageTemplate = (props) => {
    const { tableRef, getData, entityName, entityId } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const newEditor = useRef(null);
    const formRef = useRef();

    const [language, setLanguage] = useState(null);
    const [templateCode, setTemplateCode] = useState(null);
    const [emailFrom, setEmailFrom] = useState(null);
    const [emailSubject, setEmailSubject] = useState(null);
    const [emailBody, setEmailBody] = useState("");
    const [isCodeViewEnabled, setIsCodeViewEnabled] = useState(false);
    const [templateField, setTemplateField] = useState(null);


    const {
        openModal,
        loading,
        create,
        update,
        messageTemplate,
        current,
    } = useSelector(state => state.createEditMessageTemplateRedux);
    const lang = useSelector(state => state.commonRedux.language);

    const getSunEditorInstance = (sunEditor) => {
        newEditor.current = sunEditor;

        newEditor.current.toggleCodeView = function (isCodeView, core) {
            setIsCodeViewEnabled(isCodeView);
        }
    };

    const closeModal = () => {
        setLanguage(null);
        setTemplateCode(null);
        setTemplateField(null);
        setEmailFrom(null);
        setEmailSubject(null);
        setEmailBody("");
        setIsCodeViewEnabled(false);
        dispatch(clearRedux("MESSAGE_TEMPLATE_CREATE_EDIT"))
    }

    const applyChanges = () => {
        let payload = {
            emailFrom: emailFrom,
            emailSubject: emailSubject,
            emailBody: emailBody
        }
        if (current?.id) {
            payload.id = current?.id
            dispatch(messageTemplateEditRequest(payload))
        } else {
            payload.entityNameId = `${entityName}Id_${entityId}`
            payload.templateCode = templateCode?.id
            payload.language = language?.id

            dispatch(messageTemplateCreateRequest(payload))
        }
    }

    useEffect(() => {
        if (create) {
            if (!create?.error) {
                NotificationManager.success(
                    messages['message_template_create_success_message'],
                    messages['message_template_create_title'],
                    3000
                );
                closeModal();
                tableRef && tableRef?.current?.getData();
                dispatch(clearRedux("MESSAGE_TEMPLATE_CREATE_EDIT", { create: null }))
            } else {
                let message = create?.error?.includes("duplicated") ? messages['message_template_create_duplicate_code'] : messages['message_template_create_error_message'];
                NotificationManager.error(
                    message,
                    messages['message_template_create_title'],
                    3000
                );
            }
        }

        if (update) {
            NotificationManager.success(
                messages['message_template_edit_success_message'],
                messages['message_template_edit_title'],
                3000
            );
            closeModal();
            tableRef && tableRef?.current?.getData();
            getData && getData()
            dispatch(clearRedux("MESSAGE_TEMPLATE_CREATE_EDIT", { update: null }))

        }

        if (messageTemplate) {
            let tmpleCode = getMessageTemplateCodes(messages).find(item => item.id === messageTemplate?.templateCode);
            let lang = languageOptions(messages).find(item => item.id === messageTemplate?.language);
            setTemplateCode(tmpleCode);
            setTemplateField(null);
            setLanguage(lang);
            setEmailFrom(messageTemplate?.emailFrom);
            setEmailSubject(messageTemplate?.emailSubject);
            setEmailBody(messageTemplate?.emailBody);
        }
    }, [create, update, current, messageTemplate])

    useEffect(() => {
        if (openModal && current?.id) {
            dispatch(messageTemplateGetRequest(current?.id))
        }
    }, [openModal])

    return (
        <Modal
            open={openModal}
            title={current?.id ? `${messages['message_template_edit_title']} (${templateCode?.label} - ${language?.name})` : messages['message_template_create_title']}
            handleClose={() => closeModal()}
            position="right"
            loading={loading}
            size="large"
        >
            <ModalBody>
                <div style={{ padding: '0 10px', height: 680 }}>
                    <Form
                        ref={formRef}
                        onValidSubmit={() => {
                            let valid = true
                            let templateFields = getTemplatesFields(messages)[templateCode?.id]

                            templateFields?.forEach((item) => {
                                if (emailBody === "") {
                                    valid = false
                                }else if (!emailBody?.includes(`{{${item.id}}}`)) {
                                    valid = false

                                }
                            })

                            if (valid) {
                                applyChanges()
                            } else{
                                NotificationManager.error(
                                    messages['message_template_create_missing_template_fields'],
                                    messages['message_template_create_title'],
                                    3000
                                );
                            }
                        }}
                    >
                        <Grid
                            container
                            spacing={1}
                            style={{
                                backgroundColor: '#fafafa',
                                margin: 0,
                                border: '1px solid #dadada',
                                borderBottom: 0
                            }}
                        >
                            <Grid item xs={6}>
                                <div style={{ margin: 10 }}>
                                    <Select
                                        id="template-code"
                                        value={templateCode}
                                        options={getMessageTemplateCodes(messages)}
                                        label={messages['message_template_template']}
                                        onChange={(value) => {
                                            setTemplateCode(value)
                                            setTemplateField(null)
                                            newEditor.current?.setContents("")
                                            setEmailBody("")
                                        }}
                                        valueField="id"
                                        textField="label"
                                        placeholder={messages['message_template_template']}
                                        required
                                        requiredMessage={messages['required_field']}
                                        disabled={current?.id}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <div style={{ margin: 10 }}>
                                    <Select
                                        id="language"
                                        value={language}
                                        options={languageOptions(messages)}
                                        label={messages['message_template_language']}
                                        onChange={(value) => setLanguage(value)}
                                        valueField="id"
                                        textField="name"
                                        placeholder={messages['message_template_language']}
                                        required
                                        requiredMessage={messages['required_field']}
                                        disabled={current?.id}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <div style={{ margin: 10 }}>
                                    <Input
                                        label={messages['message_template_email_from']}
                                        placeholder={messages['message_template_email_from']}
                                        value={emailFrom}
                                        onChange={setEmailFrom}
                                        required
                                        requiredMessage={messages['required_field']}
                                        email
                                        emailMessage={messages['email_is_invalid']}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <div style={{ margin: 10 }}>
                                    <Input
                                        label={messages['message_template_email_subject']}
                                        placeholder={messages['message_template_email_subject']}
                                        value={emailSubject}
                                        onChange={setEmailSubject}
                                        maxLength={250}
                                        required
                                        requiredMessage={messages['required_field']}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <div style={{ margin: 10 }}>
                                    <Select
                                        id="fields-templates"
                                        value={templateField}
                                        options={templateCode && getTemplatesFields(messages)[templateCode?.id]}
                                        label={messages['messages_templates_template_fields']}
                                        onChange={(value) => {
                                            setTemplateField(value)
                                            newEditor.current?.insertHTML(`{{${value?.id}}}`)
                                        }}
                                        valueField="id"
                                        textField="name"
                                        placeholder={messages['messages_templates_template_fields']}

                                    />
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ margin: 10 }}>
                                    <label style={{ marginRight: 5 }} className="input-normal-label">{messages['message_template_email_body']}</label>
                                </div>
                            </Grid>
                            <Grid item xs={12}>
                                <div style={{ margin: 10 }}>
                                    <SunEditor
                                        lang={lang}
                                        setContents={emailBody}
                                        onChange={(content) => {
                                            setEmailBody(content)
                                        }}
                                        getSunEditorInstance={getSunEditorInstance}
                                        height="calc(100vh - 180px)"
                                        setOptions={{
                                            height: "100%",
                                            buttonList: [
                                                [
                                                    "bold",
                                                    "underline",
                                                    "italic",
                                                    "strike",
                                                    "subscript",
                                                    "superscript",
                                                    "fontColor",
                                                    "hiliteColor",
                                                    "textStyle",
                                                    "removeFormat",
                                                    "outdent",
                                                    "align",
                                                    "indent",
                                                    "list",
                                                    "fontSize",
                                                    "formatBlock",
                                                    "link",
                                                    "table",
                                                    "video",
                                                    "codeView",
                                                    "showBlocks"
                                                ]
                                            ]
                                        }}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </Form>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="btn btn-cancel"
                    onClick={() => closeModal()}
                >
                    {messages['common_cancel']}
                </Button>
                <Button
                    className="btn-dark-blue"
                    disabled={isCodeViewEnabled}
                    onClick={() => {
                        formRef?.current?.submit()
                    }}
                >
                    {messages['save']}
                </Button>
            </ModalFooter>
        </Modal>
    )
}

export default CreateEditMessageTemplate;